import { useCallback } from "react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CheckCircle, ExternalLink, Mail, Sparkles } from "lucide-react";
import type { IntakeData } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { BackButton } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";

interface OtherSpecialtyThankYouCardProps {
  data: IntakeData;
  onBack: () => void;
}

export function OtherSpecialtyThankYouCard({
  data,
  onBack,
}: OtherSpecialtyThankYouCardProps) {
  const { _ } = useLingui();
  const websiteUrl =
    import.meta.env.VITE_JEMA_WEBSITE_URL || "https://jemaservices.com.co";

  const firstName = (data.firstName ?? "").trim() || "—";

  const goToWebsite = useCallback(() => {
    window.location.href = websiteUrl;
  }, [websiteUrl]);

  useIntakeMobileChromeRegistration(
    () => ({
      onCancel: onBack,
      onContinue: goToWebsite,
      continueLabel: _(msg`Return to website`),
      continueShowKeyboardHint: false,
    }),
    [onBack, goToWebsite, _],
  );

  return (
    <div className="mx-auto w-full max-w-lg">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-[#166B93]/12 bg-gradient-to-b from-[#166B93]/[0.06] via-white to-white px-6 pb-10 pt-12 text-center shadow-[0_8px_40px_-12px_rgba(22,107,147,0.25)] sm:px-10 sm:pb-12 sm:pt-14">
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#166B93]/10 blur-2xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-[#166B93]/5 blur-xl"
          aria-hidden
        />

        <div className="relative mb-6 flex justify-center">
          <div className="relative">
            <div className="absolute inset-[-6px] rounded-full bg-[#166B93]/15 blur-lg" aria-hidden />
            <div className="relative flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full bg-gradient-to-br from-[#166B93] to-[#12587A] shadow-xl shadow-[#166B93]/35 ring-4 ring-white">
              <CheckCircle className="h-[2.35rem] w-[2.35rem] text-white" strokeWidth={2.25} />
            </div>
          </div>
        </div>

        <div className="relative space-y-3">
          <p className="inline-flex items-center gap-1.5 rounded-full border border-[#166B93]/15 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#166B93] shadow-sm backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            <Trans>Inquiry received</Trans>
          </p>
          <h1 className="text-[clamp(1.5rem,5vw,2.25rem)] font-bold tracking-tight text-gray-900">
            <Trans>Thank you</Trans>
          </h1>
          <p className="text-lg font-semibold text-[#166B93] sm:text-xl">
            <Trans>Your other-specialty inquiry has been sent</Trans>
          </p>
          <p className="mx-auto max-w-md pt-1 text-[15px] leading-relaxed text-gray-600">
            <Trans>
              Thank you, <span className="font-semibold text-gray-900">{firstName}</span>.
              We’ve shared your details with our team. Someone will follow up using
              the contact information you provided.
            </Trans>
          </p>
        </div>
      </div>

      {/* Next steps */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-gray-100/80 bg-white shadow-sm">
        <div className="border-b border-gray-100 bg-gray-50/80 px-5 py-3.5 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#166B93]/10">
              <Mail className="h-5 w-5 text-[#166B93]" aria-hidden />
            </div>
            <h2 className="text-left text-sm font-bold uppercase tracking-wider text-gray-500">
              <Trans>What happens next</Trans>
            </h2>
          </div>
        </div>
        <ul className="space-y-0 divide-y divide-gray-50 px-5 py-1 sm:px-6">
          <li className="flex gap-3 py-4 text-left text-sm leading-relaxed text-gray-600">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-xs font-bold text-emerald-700">
              1
            </span>
            <span>
              <Trans>
                Watch your email — including spam or promotions — for a reply from
                our team.
              </Trans>
            </span>
          </li>
          <li className="flex gap-3 py-4 text-left text-sm leading-relaxed text-gray-600">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-xs font-bold text-emerald-700">
              2
            </span>
            <span>
              <Trans>
                This request was sent outside the standard intake system, so you
                won’t see it in the Jema manager portal.
              </Trans>
            </span>
          </li>
        </ul>
      </div>

      {/* Back + CTA — md+: same row as CardActions; mobile: sticky footer only */}
      <div className="card-actions-row mt-8 hidden w-full max-w-lg flex-row flex-nowrap items-center gap-1.5 px-1 sm:gap-3 md:flex [&>button]:!m-0 mx-auto">
        <BackButton onClick={onBack} />
        <div className="min-w-0 flex-1 md:flex-initial md:w-auto md:min-w-0 [&>button]:!m-0">
          <Button
            type="button"
            size="lg"
            className="h-12 w-full min-w-[min(100%,200px)] rounded-2xl bg-[#166B93] px-6 text-base font-semibold shadow-lg shadow-[#166B93]/25 transition hover:bg-[#145d82] hover:shadow-xl hover:shadow-[#166B93]/30 md:w-auto md:px-8"
            onClick={goToWebsite}
          >
            <ExternalLink className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            <Trans>Return to website</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}
